import { useCallback, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Lightbox from "yet-another-react-lightbox";
import Counter from "yet-another-react-lightbox/plugins/counter";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/counter.css";
import { useT } from "../../i18n";
import { ResponsiveImage } from "../ResponsiveImage";

const ARROW = "absolute top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center bg-ink/55 text-white backdrop-blur-md transition-[background-color,opacity] duration-300 hover:bg-ink/80 lg:opacity-0 lg:group-hover:opacity-100 lg:focus-visible:opacity-100";

export const Gallery = ({ images, index, setIndex, lightboxOpen, setLightboxOpen, title }) => {
  const t = useT();
  const stripRef = useRef(null);
  const touchX = useRef(null);
  const count = images.length;
  const go = useCallback((d) => setIndex((i) => (i + d + count) % count), [count, setIndex]);

  useEffect(() => {
    const strip = stripRef.current;
    const el = strip?.children[index];
    if (!strip || !el) return;
    strip.scrollTo({ left: el.offsetLeft - strip.clientWidth / 2 + el.clientWidth / 2, behavior: "smooth" });
  }, [index]);

  if (!count) {
    return (
      <div className="flex aspect-[3/2] items-center justify-center bg-graphite" data-testid="gallery-empty">
        <span className="micro-label text-white/40">{t("detail.noImages")}</span>
      </div>
    );
  }

  const current = images[index] || images[0];
  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") go(1);
    if (e.key === "ArrowLeft") go(-1);
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };

  return (
    <div data-testid="vehicle-gallery">
      <div
        className="group relative aspect-[3/2] overflow-hidden bg-graphite"
        role="region"
        aria-roledescription="carousel"
        aria-label={title}
        onKeyDown={onKeyDown}
        onTouchStart={(e) => {
          touchX.current = e.touches[0].clientX;
        }}
        onTouchEnd={onTouchEnd}
      >
        <button type="button" onClick={() => setLightboxOpen(true)} aria-label={t("detail.openGallery")} className="absolute inset-0 h-full w-full cursor-zoom-in" data-testid="gallery-main">
          <ResponsiveImage key={current.id} image={current} maxVariant="medium" sizes="(min-width: 1024px) 66vw, 100vw" eager={index === 0} className="h-full w-full object-cover" testId="gallery-main-image" />
        </button>
        {count > 1 && (
          <>
            <button type="button" onClick={() => go(-1)} aria-label={t("detail.previous")} className={`${ARROW} left-3`} data-testid="gallery-prev">
              <ChevronLeft size={20} strokeWidth={1.25} />
            </button>
            <button type="button" onClick={() => go(1)} aria-label={t("detail.next")} className={`${ARROW} right-3`} data-testid="gallery-next">
              <ChevronRight size={20} strokeWidth={1.25} />
            </button>
          </>
        )}
        <span className="micro-label pointer-events-none absolute bottom-3 right-3 bg-ink/60 px-2.5 py-1.5 tabular-nums text-white backdrop-blur-md" data-testid="gallery-counter">
          {index + 1} / {count}
        </span>
      </div>
      {count > 1 && (
        <div ref={stripRef} className="no-scrollbar mt-2 flex gap-2 overflow-x-auto" data-testid="gallery-thumbs">
          {images.map((im, i) => (
            <button
              key={im.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`${i + 1} / ${count}`}
              aria-current={i === index ? "true" : undefined}
              data-testid={`gallery-thumb-${i}`}
              className={`relative h-[60px] w-[90px] shrink-0 overflow-hidden bg-graphite transition-opacity duration-300 ${
                i === index ? "opacity-100 outline outline-1 -outline-offset-1 outline-white" : "opacity-45 hover:opacity-85"
              }`}
            >
              <ResponsiveImage image={im} maxVariant="thumb" sizes="90px" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={index}
        slides={images.map((im) => ({ src: im.variants.large, alt: im.alt, width: im.width || 1264, height: im.height || 848 }))}
        plugins={[Counter]}
        carousel={{ preload: 1, finite: count <= 1 }}
        controller={{ closeOnBackdropClick: true }}
        on={{ view: ({ index: i }) => setIndex(i) }}
        styles={{ container: { backgroundColor: "rgba(8, 8, 8, 0.97)" } }}
      />
    </div>
  );
};
